<template>
    <loading-indicator v-if="loading" text="Lade Web-Exporte"/>
    <div v-else>
        <template v-if="availableExports.length">
            <table class="full-width">
                <tr v-for="exportSetting in availableExports">
                    <td>Webexport ({{viewTypeName(exportSetting.name as ViewTypeOptionKey)}})</td>
                    <td class="calendar-export-state">
                        <span :class="['export-state', {'export-state-disabled': !exportSetting.active}]">
                            {{exportSetting.active ? 'Aktiv' : 'Inaktiv'}}
                        </span>
                    </td>
                    <td class="text-right">
                        <a @click="editExport(exportSetting)">
                            <i class="material-icons md-16" aria-hidden="true" role="img">settings</i>
                            Bearbeiten
                        </a>
                    </td>
                </tr>
            </table>
        </template>
        <div v-else>
            Es wurden noch keine Webseiten-Exporte erstellt.
        </div>

        <button v-if="!inPopUp && nonAvailableExports.length" class="button button-secondary long spacer-15" @click="createExport">
            <span class="visible-xl-inline-upward">Export</span> erstellen
        </button>

        <app-popup v-model="showCreateExportPopup" title="Export erstellen" title-icon="public" :buttons="createButtons" size="small">
            <form-row label="Standard-Ansicht" label-for="view-select" help="Diese Ansicht wird geöffnet, sofern der Benutzer das erste Mal auf die Seite geht."
                class="no-margin" required>
                <form-select :options="nonAvailableExports" v-model="createExportView"/>
            </form-row>
        </app-popup>
    </div>
</template>

<script setup lang="ts">
    import {usePortuna} from '@/composables/portuna';
    import axios, {type AxiosError, type AxiosResponse} from 'axios';
    import {defineAsyncComponentWrapper} from '@/library/utils';
    import FormRow from '@/components/form/FormRow.vue';
    import FormSelect from '@/components/form/FormSelect.vue';
    import LoadingIndicator from '@/components/LoadingIndicator.vue';
    import {exportPopUp} from '@/library/intranet/export';
    import {viewTypeOptions} from '@/library/modules/calendar/calendar';
    import {popupDefaultCancelButton, popupDefaultCloseButton} from '@/library/popup';
    import {errorHandler, XHR_STATUS_DEFAULT} from '@/utils/portuna';
    import type {TinyEmitter} from 'tiny-emitter';
    import {ref, computed, watch, toRefs, inject} from 'vue';

    const portuna = usePortuna();

    const AppPopup = defineAsyncComponentWrapper(() => import('@/components/AppPopup.vue'));

    interface CalendarExportSettings
    {
        name: string;
        active: boolean;
        label?: string;
    }

    const props = defineProps<{
        resource: string;
        id: number;
        inPopUp?: boolean;
    }>();

    const {
              resource,
              id,
              inPopUp,
          } = toRefs(props);

    let popupEmitter = null;
    if (inPopUp.value!)
    {
        popupEmitter = inject<TinyEmitter>('popup-emitter')!;
    }

    const exports               = ref<Record<string, ExportSettings | false>>({});
    const showCreateExportPopup = ref(false);
    const createButtons         = ref<PopupButton[]>([popupDefaultCloseButton]);
    const createExportView      = ref('');
    const loading               = ref(true);

    const viewTypeName = (type: ViewTypeOptionKey): string =>
    {
        return (viewTypeOptions[type] && viewTypeOptions[type].name) || '';
    };

    const availableExports = computed((): CalendarExportSettings[] =>
    {
        let newExports = [];
        for (const name of Object.keys(exports.value!))
        {
            if (!exports.value!.hasOwnProperty(name))
            {
                continue;
            }

            const exportSetting = exports.value![name];
            if (exportSetting)
            {
                newExports.push({
                    name,
                    active: exportSetting.active,
                });
            }
        }

        return newExports;
    });

    const nonAvailableExports = computed((): SelectOption[] =>
    {
        let newExports = [];
        for (const name of Object.keys(exports.value!))
        {
            if (!exports.value!.hasOwnProperty(name))
            {
                continue;
            }

            const exportSetting = exports.value![name];
            if (!exportSetting)
            {
                newExports.push({
                    value: name,
                    label: viewTypeName(name as ViewTypeOptionKey),
                });
            }
        }

        return newExports;
    });

    const onError = (e: AxiosError | AxiosResponse) =>
    {
        if (popupEmitter)
        {
            popupEmitter.emit('close');
        }
        else
        {
            portuna.emitter.emit('close');
        }

        if (inPopUp.value!)
        {
            errorHandler(e);
        }
    };

    axios
    .get('/' + resource.value! + '/calendar/fetch-exports/id/' + id.value!)
    .then((r) =>
    {
        if (r.data.status === XHR_STATUS_DEFAULT)
        {
            exports.value! = r.data.data.exports;
        }
        else
        {
            onError(r);
        }
    })
    .catch(onError)
    .finally(() =>
    {
        loading.value = false;
    });

    const editExport = (exportSettings: CalendarExportSettings) =>
    {
        exportPopUp(
            {
                resource: resource.value!,
                route: resource.value! + '/main/wildcard',
                internal_id: id.value!,
                parameters: {
                    action: 'export-list',
                    id: id.value!,
                    view: exportSettings.name,
                },
            },
            {
                events: {
                    onGenerated: (settings: ExportSettings) =>
                    {
                        exports.value![exportSettings.name] = settings;
                    },
                    onDeleted: () =>
                    {
                        exports.value![exportSettings.name] = false;
                    },
                },
                props: {
                    askBeforeCreate: false,
                },
            },
        );
    };

    const createExport = () =>
    {
        showCreateExportPopup.value = true;
    };

    const changeExportView = () =>
    {
        if (createExportView.value)
        {
            createButtons.value = [
                popupDefaultCancelButton,
                {
                    type: 'primary',
                    title: 'Erstellen',
                    icon: 'add',
                    cmd: () =>
                    {
                        showCreateExportPopup.value = false;
                        editExport({
                            name: createExportView.value,
                            active: true,
                        });
                        createExportView.value = '';
                    },
                },
            ];
        }
        else
        {
            createButtons.value = [popupDefaultCloseButton];
        }
    };

    const watchExports = () =>
    {
        if (nonAvailableExports.value.length)
        {
            if (popupEmitter)
            {
                popupEmitter.emit('setButtons', [
                    popupDefaultCancelButton,
                    {
                        type: 'primary',
                        title: 'Export erstellen',
                        icon: 'language',
                        cmd: createExport,
                    },
                ]);
            }
            else
            {
                portuna.emitter.emit('setButtons', [
                    popupDefaultCancelButton,
                    {
                        type: 'primary',
                        title: 'Export erstellen',
                        icon: 'language',
                        cmd: createExport,
                    },
                ]);
            }
        }
        else
        {
            if (popupEmitter)
            {
                popupEmitter.emit('setButtons', [popupDefaultCloseButton]);
            }
            else
            {
                portuna.emitter.emit('setButtons', [popupDefaultCloseButton]);
            }
        }
    };

    watch(createExportView, changeExportView);
    watch(nonAvailableExports, watchExports);

    defineExpose(Object.assign({}, portuna));
</script>